<script>
import Layout from "@/router/layouts/main";
import StepperNav from "@/components/energia/StepperNav.vue";
import UploadZone from "@/components/energia/UploadZone.vue";
import { CONCESSIONARIAS, carregarConcessionarias, matchConcessionaria } from "@/data/energia/concessionarias";
import { api } from "@/state/services";
import { formatCpfCnpjDisplay } from "@/utils/energia/cpfCnpj";
import { extractBillFromPdf } from "@/utils/energia/billExtractor";
import {
  BANDEIRAS,
  buscarTarifaBaseConcessionaria,
  extrairTarifaBase,
  estimarTarifaFallback,
  calcularEconomia,
  consumoMedio,
  gerarResumoSimulacao,
} from "@/utils/energia/simulador";

export default {
  components: { Layout, StepperNav, UploadZone },

  data() {
    return {
      etapaAtual: 1,
      etapasConcluidas: [],
      saving: false,

      extraindo: false,
      erroExtracao: '',
      dadosExtraidos: null,
      extracaoFromCache: false,
      concessionariaNaoIdentificada: '',
      simulacao: null,
      modoManual: false,

      modoInstalacaoExtra: false,
      clienteExistente: null,
      carregandoCliente: false,

      // Etapas: 1=Conta, 2=Extração, 3=Simulação, 4=Dados, 5=Documentos, 6=Instalação, 7=Revisão
      etapasFull: [
        { numero: 1, label: 'Conta' },
        { numero: 2, label: 'Extração' },
        { numero: 3, label: 'Simulação' },
        { numero: 4, label: 'Dados' },
        { numero: 5, label: 'Documentos' },
        { numero: 6, label: 'Instalação' },
        { numero: 7, label: 'Revisão' },
      ],

      form: {
        // Etapa 4 — dados pessoais
        person_type: 'pf',
        person_number: '',
        nome: '',
        telefone: '',
        email: '',
        data_nascimento: '',
        nacionalidade: '',
        estado_civil: '',
        observacoes: '',

        // Etapa 5 — documentos
        tipo_documento: 'cnh',
        arquivo_doc_frente: null,
        arquivo_doc_frente_nome: '',
        arquivo_doc_verso: null,
        arquivo_doc_verso_nome: '',

        // Etapa 6 — instalação
        concessionaria_id: '',
        tipo_fase: '',
        numero_instalacao: '',
        kwh_avg: '',
        mes_referencia: '',
        valor_fatura: '',
        arquivo_conta: null,
        arquivo_conta_nome: '',
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
        obs_instalacao: '',
        portal_usuario: '',
        portal_senha: '',
      },

      concessionarias: CONCESSIONARIAS,
    }
  },

  computed: {
    etapas() {
      if (this.modoInstalacaoExtra) {
        return this.etapasFull.filter(e => e.numero !== 4 && e.numero !== 5)
      }
      return this.etapasFull
    },

    personNumberMask() {
      return this.form.person_type === 'pj' ? '##.###.###/####-##' : '###.###.###-##'
    },

    nomeRazaoLabel() {
      return this.form.person_type === 'pj' ? 'Razão social' : 'Nome completo'
    },

    nomeConcessionaria() {
      const c = this.concessionarias.find(x => String(x.id) === String(this.form.concessionaria_id))
      return c ? c.nome : ''
    },

    tituloPagina() {
      if (this.modoInstalacaoExtra && this.clienteExistente) {
        return `Nova instalação para ${this.clienteExistente.name}`
      }
      return 'Novo cadastro'
    },

    rotaCancelar() {
      if (this.modoInstalacaoExtra && this.clienteExistente) {
        return '/energia/clientes/' + this.clienteExistente.id
      }
      return '/energia/clientes'
    },

    // ── Simulação (computed) ────────────────────────────────────

    // Em modo manual, constrói um JSON mínimo com os campos preenchidos pelo operador
    extractedJsonEfetivo() {
      if (!this.modoManual) return this.dadosExtraidos
      const consumo  = parseFloat(String(this.form.kwh_avg || '').replace(',', '.'))
      const totalDue = parseFloat(String(this.form.valor_fatura || '').replace(',', '.'))
      if (!consumo || !totalDue) return null
      const conc = this.concessionarias.find(c => String(c.id) === String(this.form.concessionaria_id))
      return {
        consumption: { consumed_kwh: consumo },
        total_due:   totalDue,
        company_name: conc ? conc.nome : '',
      }
    },

    tarifaBase() {
      const json = this.extractedJsonEfetivo
      if (!json) return null
      const fromCsv = buscarTarifaBaseConcessionaria({
        companyName:      json.company_name,
        concessionariaId: this.form.concessionaria_id,
      })
      if (fromCsv != null) return fromCsv
      const fromCharges = extrairTarifaBase(json.charges)
      if (fromCharges) return fromCharges
      return estimarTarifaFallback(json.total_due, this.consumoUltima)
    },

    consumoUltima() {
      const json = this.extractedJsonEfetivo
      const v = json?.consumption?.consumed_kwh
      const n = parseFloat(String(v || '').replace(',', '.'))
      return Number.isFinite(n) && n > 0 ? n : null
    },

    consumoMedioVal() {
      if (this.modoManual) return null
      if (!this.dadosExtraidos?.consumption) return null
      return consumoMedio(this.dadosExtraidos.consumption)
    },

    faseSimulacao() {
      return this.form.tipo_fase || 'monofasico'
    },

    simDadosValidos() {
      return this.tarifaBase != null && this.consumoUltima != null
    },

    bandeiras() {
      return Object.entries(BANDEIRAS).map(([key, info]) => ({ key, ...info }))
    },

    linhasUltima() {
      if (!this.simDadosValidos) return null
      return this.calcLinhasSimulacao(this.consumoUltima)
    },

    linhasMedia() {
      if (!this.simDadosValidos || this.consumoMedioVal == null) return null
      return this.calcLinhasSimulacao(this.consumoMedioVal)
    },

    dadosFatura() {
      const j = this.extractedJsonEfetivo
      if (!j) return null
      const addr = j.address || {}
      const partes = [addr.street, addr.number, addr.complement].filter(Boolean).join(', ')
      const cidade = [addr.neighborhood, addr.city, addr.state].filter(Boolean).join(' — ')
      return {
        nome:          j.customer_name || this.form.nome || '',
        concessionaria: j.company_name || '',
        referencia:    j.billing_period?.reference || '',
        endereco:      partes,
        cidade,
        cep:           addr.zip_code ? String(addr.zip_code).replace(/(\d{5})(\d{3})/, '$1-$2') : '',
        totalFatura:   j.total_due || '',
        vencimento:    j.due_date || '',
      }
    },
  },

  created() {
    carregarConcessionarias().catch(() => {
      this.$toast.error('Não foi possível carregar as concessionárias. Recarregue a página.')
    })

    const idclient = this.$route.params.idclient
    if (idclient) {
      this.modoInstalacaoExtra = true
      this.carregarCliente(idclient)
    }
  },

  methods: {
    formatCpfCnpjDisplay,

    carregarCliente(idclient) {
      this.carregandoCliente = true
      api
        .get('energy/clients/' + idclient)
        .then(response => {
          if (response.data.status === 'success') {
            const c = response.data.client
            this.clienteExistente = c
            this.form.person_type    = c.person_type || 'pf'
            this.form.person_number  = c.person_number || ''
            this.form.nome           = c.name || ''
            this.form.telefone       = c.cellphone || ''
            this.form.email          = c.email || ''
            this.form.tipo_documento = c.document_type || 'cnh'
            if (!this.etapasConcluidas.includes(4)) this.etapasConcluidas.push(4)
            if (!this.etapasConcluidas.includes(5)) this.etapasConcluidas.push(5)
          } else {
            this.$toast.error(response.data.message || 'Cliente não encontrado.')
            this.$router.push('/energia/clientes')
          }
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar dados do cliente.')
          this.$router.push('/energia/clientes')
        })
        .finally(() => {
          this.carregandoCliente = false
        })
    },

    setPersonType(type) {
      if (this.form.person_type !== type) {
        this.form.person_type = type
        this.form.person_number = ''
        if (type === 'pj') {
          this.form.nacionalidade   = ''
          this.form.estado_civil    = ''
          this.form.data_nascimento = ''
        }
      }
    },

    // Número do endereço aceita só dígitos: remove as letras digitadas e avisa
    filtrarNumero(event) {
      const original = event.target.value
      const digits   = original.replace(/\D/g, '')

      if (digits !== original) {
        this.$toast.error('O campo número aceita apenas números. Se o endereço não tiver número, marque "Sem número".')
      }

      this.form.numero   = digits
      event.target.value = digits
    },

    toggleSemNumero(checked) {
      this.form.numero = checked ? 'S/N' : ''
    },

    resetCamposExtraidos() {
      if (!this.modoInstalacaoExtra) {
        this.form.person_type      = 'pf'
        this.form.person_number    = ''
        this.form.nome             = ''
        this.form.data_nascimento  = ''
      }
      this.form.concessionaria_id = ''
      this.form.tipo_fase         = ''
      this.form.numero_instalacao = ''
      this.form.kwh_avg           = ''
      this.form.mes_referencia    = ''
      this.form.valor_fatura      = ''
      this.form.logradouro        = ''
      this.form.numero            = ''
      this.form.complemento       = ''
      this.form.bairro            = ''
      this.form.cidade            = ''
      this.form.estado            = ''
      this.form.cep               = ''
      this.dadosExtraidos         = null
      this.extracaoFromCache      = false
      this.simulacao              = null
      this.modoManual             = false
      this.concessionariaNaoIdentificada = ''
    },

    onContaUpload(file) {
      this.resetCamposExtraidos()
      this.form.arquivo_conta = file
      this.form.arquivo_conta_nome = file.name
    },

    onContaRemover() {
      this.form.arquivo_conta = null
      this.form.arquivo_conta_nome = ''
    },

    onDocUpload(key, file) {
      if (file.size > 100 * 1024 * 1024) {
        this.$toast.error('Arquivo muito grande. Máximo 100MB.')
        return
      }
      this.form[key] = file
      this.form[key + '_nome'] = file.name
    },

    onDocRemover(key) {
      this.form[key] = null
      this.form[key + '_nome'] = ''
    },

    async buscarCep(cep) {
      const c = (cep || '').replace(/\D/g, '')
      if (c.length !== 8) return
      try {
        const r = await fetch(`https://viacep.com.br/ws/${c}/json/`)
        const d = await r.json()
        if (!d.erro) {
          this.form.logradouro = d.logradouro
          this.form.bairro     = d.bairro
          this.form.cidade     = d.localidade
          this.form.estado     = d.uf
        }
      } catch { /* silencia */ }
    },

    normalizarTexto(s) {
      return String(s || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },

    matchConcessionaria,

    parseNumber(value) {
      if (value == null || value === '') return null
      if (typeof value === 'number') return Number.isFinite(value) ? value : null
      const cleaned = String(value)
        .replace(/[^\d,.-]/g, '')
        .replace(/\.(?=\d{3}(\D|$))/g, '')
        .replace(',', '.')
      const n = Number(cleaned)
      return Number.isFinite(n) ? n : null
    },

    validarCpfCnpj(doc) {
      if (!doc || (doc.length !== 11 && doc.length !== 14)) return false
      if (/^(\d)\1+$/.test(doc)) return false

      if (doc.length === 11) {
        let sum = 0
        for (let i = 0; i < 9; i++) sum += parseInt(doc[i]) * (10 - i)
        let r = (sum * 10) % 11
        if (r === 10 || r === 11) r = 0
        if (r !== parseInt(doc[9])) return false
        sum = 0
        for (let i = 0; i < 10; i++) sum += parseInt(doc[i]) * (11 - i)
        r = (sum * 10) % 11
        if (r === 10 || r === 11) r = 0
        return r === parseInt(doc[10])
      }

      const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
      const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
      let sum = pesos1.reduce((acc, p, i) => acc + parseInt(doc[i]) * p, 0)
      let r = sum % 11
      const d13 = r < 2 ? 0 : 11 - r
      if (d13 !== parseInt(doc[12])) return false
      sum = pesos2.reduce((acc, p, i) => acc + parseInt(doc[i]) * p, 0)
      r = sum % 11
      const d14 = r < 2 ? 0 : 11 - r
      return d14 === parseInt(doc[13])
    },

    parseReferenceToYYYYMM(ref) {
      if (!ref) return ''
      const s = String(ref).trim().toUpperCase()
      const meses = {
        JAN: '01', FEV: '02', MAR: '03', ABR: '04', MAI: '05', JUN: '06',
        JUL: '07', AGO: '08', SET: '09', OUT: '10', NOV: '11', DEZ: '12',
      }
      let m = s.match(/^([A-Z]{3,})\D+(\d{4})$/)
      if (m) {
        const mes = meses[m[1].slice(0, 3)]
        if (mes) return `${m[2]}-${mes}`
      }
      m = s.match(/^(\d{1,2})\D+(\d{4})$/)
      if (m) return `${m[2]}-${String(m[1]).padStart(2, '0')}`
      m = s.match(/^(\d{4})-(\d{1,2})$/)
      if (m) return `${m[1]}-${String(m[2]).padStart(2, '0')}`
      return ''
    },

    mapearJsonParaForm(json) {
      if (!json) return

      // ===== Cliente — apenas no modo cadastro completo =====
      if (!this.modoInstalacaoExtra) {
        if (json.customer_name) this.form.nome = json.customer_name

        if (json.cpf_cnpj) {
          let doc = String(json.cpf_cnpj).replace(/\D/g, '')
          if (doc.length === 13 && this.validarCpfCnpj('0' + doc)) doc = '0' + doc
          if (doc.length === 10 && this.validarCpfCnpj('0' + doc)) doc = '0' + doc
          if (doc && this.validarCpfCnpj(doc)) {
            // Grava com máscara; os dígitos servem só à validação/detecção de tipo
            this.form.person_number = this.formatCpfCnpjDisplay(doc)
            if (doc.length === 14) this.form.person_type = 'pj'
            else if (doc.length === 11) this.form.person_type = 'pf'
          }
        }
      }

      // ===== Concessionária =====
      this.concessionariaNaoIdentificada = ''
      let companyMatchFound = false
      if (json.company_name) {
        const match = this.matchConcessionaria(json.company_name)
        if (match) {
          this.form.concessionaria_id = match.id
          companyMatchFound = true
        } else {
          this.concessionariaNaoIdentificada = String(json.company_name).trim()
        }
      }

      // ===== Instalação =====
      // if (json.account_number) this.form.numero_instalacao = String(json.account_number)

      const history = Array.isArray(json?.consumption?.history) ? json.consumption.history : []
      const historyNums = history
        .map(h => this.parseNumber(h?.consumed_kwh))
        .filter(n => n != null)

      let avg = null
      if (historyNums.length > 0) {
        avg = historyNums.reduce((a, b) => a + b, 0) / historyNums.length
      } else {
        avg = this.parseNumber(json?.consumption?.consumed_kwh)
      }
      if (avg != null) this.form.kwh_avg = String(Math.round(avg))

      const ref = json?.billing_period?.reference
      const refYM = this.parseReferenceToYYYYMM(ref)
      if (refYM) this.form.mes_referencia = refYM

      const totalNum = this.parseNumber(json?.total_due)
      if (totalNum != null) this.form.valor_fatura = String(totalNum)

      const faseMap = { monofasico: 'monofasico', bifasico: 'bifasico', trifasico: 'trifasico' }
      const faseRaw = json?.installation?.supply_phase
      if (faseRaw && faseMap[faseRaw]) this.form.tipo_fase = faseMap[faseRaw]

      // ===== Endereço =====
      const addr = json.address || {}
      if (addr.zip_code) {
        // Grava com máscara (#####-###); ViaCEP tira a máscara só na consulta
        const cepDigits = String(addr.zip_code).replace(/\D/g, '').slice(0, 8)
        this.form.cep = cepDigits.length === 8 ? cepDigits.replace(/(\d{5})(\d{3})/, '$1-$2') : cepDigits
      }
      if (addr.street)       this.form.logradouro = addr.street
      if (addr.number)       this.form.numero = /\d/.test(String(addr.number)) ? String(addr.number) : 'S/N'
      if (addr.complement)   this.form.complemento = addr.complement
      if (addr.neighborhood) this.form.bairro = addr.neighborhood
      if (addr.city)         this.form.cidade = addr.city
      if (addr.state)        this.form.estado = String(addr.state).toUpperCase().slice(0, 2)

      if (this.modoInstalacaoExtra) return

      // Guarda 1: se o nome extraído for igual ou parte do endereço, é erro de extração
      if (this.form.nome) {
        const nome        = this.form.nome.trim().toUpperCase()
        const cidade      = (addr.city         || '').trim().toUpperCase()
        const bairro      = (addr.neighborhood || '').trim().toUpperCase()
        const logradouro  = (addr.street       || '').trim().toUpperCase()
        const nomeEhEndereco =
          (nome === cidade) ||
          (nome === bairro) ||
          (logradouro !== '' && logradouro.startsWith(nome)) ||
          (logradouro !== '' && nome === logradouro)
        if (nomeEhEndereco) {
          this.form.nome = ''
        }
      }

      // Guarda 2: se o nome extraído bater com uma concessionária conhecida
      if (this.form.nome && this.matchConcessionaria(this.form.nome)) {
        this.form.nome = ''
      }

      // Fallback: se nome vazio e company_name não identificado como concessionária
      if (!this.form.nome && !companyMatchFound && json.company_name) {
        this.form.nome = String(json.company_name).trim()
      }
    },

    // ── Simulação ──────────────────────────────────────────────

    calcLinhasSimulacao(consumo) {
      const econ = Object.keys(BANDEIRAS).map(key =>
        calcularEconomia(consumo, this.tarifaBase, key, this.faseSimulacao)
      )
      return [
        { label: 'Economia mensal',     key: 'mensal',     destaque: false },
        { label: 'Economia anual',      key: 'anual',      destaque: false },
        { label: 'Economia em 5 anos',  key: 'cinco_anos', destaque: false },
        { label: 'Economia em 10 anos', key: 'dez_anos',   destaque: true  },
      ].map(row => ({
        ...row,
        valores: econ.map(e => e[row.key]),
      }))
    },

    fmtR$(v) {
      if (v == null) return '—'
      return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    fmtKwh(v) {
      if (v == null) return '—'
      return v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    bandeiraBgStyle(key) {
      const colors = {
        verde:       'background:#28a745;color:#fff',
        amarela:     'background:#ffc107;color:#000',
        vermelha_p1: 'background:#dc3545;color:#fff',
        vermelha_p2: 'background:#7b1c2a;color:#fff',
      }
      return colors[key] || ''
    },

    // ── Fluxo de etapas ────────────────────────────────────────

    async extrairEAvancar(forceRefresh = false) {
      if (!this.form.arquivo_conta) {
        this.$toast.error('Selecione o PDF da conta de energia antes de continuar.')
        return
      }

      if (!this.etapasConcluidas.includes(1)) this.etapasConcluidas.push(1)
      this.resetCamposExtraidos()
      this.etapaAtual = 2
      this.extraindo = true
      this.erroExtracao = ''

      try {
        const { data, fromCache } = await extractBillFromPdf(this.form.arquivo_conta, { forceRefresh })

        this.dadosExtraidos = data
        this.extracaoFromCache = fromCache
        this.mapearJsonParaForm(data)

        this.simulacao = gerarResumoSimulacao({
          extractedJson:    data,
          concessionariaId: this.form.concessionaria_id,
          tipoFase:         this.form.tipo_fase,
        })

        if (!this.etapasConcluidas.includes(2)) this.etapasConcluidas.push(2)
        // Sempre vai para etapa 3 (Simulação) após extração
        this.etapaAtual = 3
      } catch (err) {
        const msg = err?.response?.data?.error?.message || err?.message || 'Erro ao processar o PDF ou ao contatar a OpenAI.'
        this.erroExtracao = msg
      } finally {
        this.extraindo = false
      }
    },

    async reprocessarPdf() {
      this.etapaAtual = 2
      await this.extrairEAvancar(true)
    },

    validarEtapa(n) {
      if (n === 1) {
        if (!this.form.arquivo_conta) {
          this.$toast.error('Selecione o PDF da conta de energia.')
          return false
        }
        return true
      }
      if (n === 2) return true
      if (n === 3) return true // simulação: sempre pode avançar

      if (n === 4) {
        const doc = (this.form.person_number || '').replace(/\D/g, '')
        const esperado = this.form.person_type === 'pj' ? 14 : 11
        if (!this.form.nome || !this.form.email) {
          this.$toast.error('Preencha o nome e o e-mail antes de avançar.')
          return false
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((this.form.email || '').trim())) {
          this.$toast.error('Informe um e-mail válido.')
          return false
        }
        if (doc.length !== esperado) {
          this.$toast.error(this.form.person_type === 'pj'
            ? 'Informe o CNPJ completo (14 dígitos).'
            : 'Informe o CPF completo (11 dígitos).')
          return false
        }
        if (!(this.form.telefone || '').replace(/\D/g, '').length) {
          this.$toast.error('Informe o telefone.')
          return false
        }
        if (this.form.person_type === 'pf') {
          if (!(this.form.nacionalidade || '').trim()) {
            this.$toast.error('Informe a nacionalidade.')
            return false
          }
          if (!this.form.estado_civil) {
            this.$toast.error('Selecione o estado civil.')
            return false
          }
        }
        return true
      }
      if (n === 5) {
        if (!this.form.arquivo_doc_frente) {
          this.$toast.error('Envie ao menos a frente do documento.')
          return false
        }
        if (this.form.tipo_documento === 'rg' && !this.form.arquivo_doc_verso) {
          this.$toast.error('Para RG é obrigatório enviar frente e verso.')
          return false
        }
        return true
      }
      if (n === 6) {
        if (!this.form.concessionaria_id || !this.form.numero_instalacao) {
          this.$toast.error('Selecione a concessionária e informe o código de instalação.')
          return false
        }
        const cep = (this.form.cep || '').replace(/\D/g, '')
        if (cep.length < 8) {
          this.$toast.error('Informe um CEP válido.')
          return false
        }
        if (!this.form.logradouro || !this.form.numero || !this.form.cidade || !this.form.estado) {
          this.$toast.error('Preencha logradouro, número, cidade e UF.')
          return false
        }
        return true
      }
      return true
    },

    async avancar() {
      if (!this.validarEtapa(this.etapaAtual)) return

      if (this.etapaAtual === 1) {
        const file = this.form.arquivo_conta
        const isPdf = file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'))
        if (!isPdf) {
          if (!this.etapasConcluidas.includes(1)) this.etapasConcluidas.push(1)
          if (!this.etapasConcluidas.includes(2)) this.etapasConcluidas.push(2)
          this.modoManual = true
          this.$toast.info('O arquivo não é um PDF. Preencha os dados para simular a economia.')
          this.etapaAtual = 3
          return
        }
        await this.extrairEAvancar()
        return
      }

      if (this.etapaAtual === 7) {
        await this.salvarTudo()
        return
      }

      // Na etapa 3 em modo manual, gera a simulação com os dados preenchidos
      if (this.etapaAtual === 3 && this.modoManual && this.extractedJsonEfetivo) {
        this.simulacao = gerarResumoSimulacao({
          extractedJson:    this.extractedJsonEfetivo,
          concessionariaId: this.form.concessionaria_id,
          tipoFase:         this.form.tipo_fase,
        })
      }

      if (!this.etapasConcluidas.includes(this.etapaAtual)) {
        this.etapasConcluidas.push(this.etapaAtual)
      }
      // modoInstalacaoExtra: da simulação (3) vai direto para instalação (6)
      if (this.modoInstalacaoExtra && this.etapaAtual === 3) {
        this.etapaAtual = 6
        return
      }
      if (this.modoInstalacaoExtra && this.etapaAtual === 6) {
        this.etapaAtual = 7
        return
      }
      this.etapaAtual++
    },

    voltar() {
      if (this.etapaAtual === 1) {
        this.$router.push(this.rotaCancelar)
        return
      }
      // etapa 4 (Dados): volta para Simulação se houve extração ou modo manual
      if (this.etapaAtual === 4) {
        this.etapaAtual = (this.dadosExtraidos || this.modoManual) ? 3 : 1
        return
      }
      // modoInstalacaoExtra: da instalação (6) volta para simulação (3)
      if (this.modoInstalacaoExtra && this.etapaAtual === 6) {
        this.etapaAtual = 3
        return
      }
      if (this.modoInstalacaoExtra && this.etapaAtual === 7) {
        this.etapaAtual = 6
        return
      }
      this.etapaAtual--
    },

    irParaEtapa(n) {
      if (n === 2) return
      if (this.modoInstalacaoExtra && (n === 4 || n === 5)) return
      if (this.etapasConcluidas.includes(n) || n === this.etapaAtual) {
        this.etapaAtual = n
      }
    },

    async salvarTudo() {
      this.saving = true
      try {
        let idClient

        if (this.modoInstalacaoExtra) {
          idClient = this.clienteExistente.id
        } else {
          const resCliente = await api.post('energy/clients/create', {
            person_type:    this.form.person_type,
            name:           this.form.nome,
            person_number:  (this.form.person_number || '').trim(),
            email:          this.form.email,
            cellphone:      (this.form.telefone || '').trim(),
            document_type:  this.form.tipo_documento,
            nationality:    this.form.nacionalidade,
            marital_status: this.form.estado_civil,
            birth_date:     this.form.person_type === 'pf' ? this.form.data_nascimento : '',
            internal_notes: (this.form.observacoes || '').trim(),
          })

          if (resCliente.data.status !== 'success') {
            this.$toast.error(resCliente.data.message || 'Erro ao cadastrar cliente.')
            return
          }

          idClient = resCliente.data.client.id

          const formData = new FormData()
          formData.append('document_type', this.form.tipo_documento)
          formData.append('doc_front', this.form.arquivo_doc_frente)
          if (this.form.arquivo_doc_verso) {
            formData.append('doc_back', this.form.arquivo_doc_verso)
          }

          const resDoc = await api.post('energy/clients/' + idClient + '/documents', formData)
          if (resDoc.data.status !== 'success') {
            this.$toast.error(resDoc.data.message || 'Erro ao enviar documentos.')
            return
          }
        }

        const kwhNum = this.form.kwh_avg !== '' && !Number.isNaN(Number(this.form.kwh_avg))
          ? Number(this.form.kwh_avg)
          : 0

        // Recalcula simulação com os dados finais (podem ter sido editados na etapa 6)
        const simFinal = gerarResumoSimulacao({
          extractedJson:    this.extractedJsonEfetivo,
          concessionariaId: this.form.concessionaria_id,
          tipoFase:         this.form.tipo_fase,
        })

        const resInstalacao = await api.post('energy/installations/create', {
          id:               idClient,
          code:             this.form.numero_instalacao,
          zipcode:          (this.form.cep || '').trim(),
          address:          this.form.logradouro,
          number:           this.form.numero,
          complement:       this.form.complemento || '',
          city:             this.form.cidade,
          state:            this.form.estado,
          distributor:      this.nomeConcessionaria,
          phase_type:       this.form.tipo_fase || '',
          kwh_avg:          kwhNum,
          portal_username:  this.form.portal_usuario,
          portal_password:  this.form.portal_senha,
          simulation_json:  simFinal ? JSON.stringify(simFinal) : '',
        })

        if (resInstalacao.data.status !== 'success') {
          this.$toast.error(resInstalacao.data.message || 'Erro ao cadastrar instalação.')
          return
        }

        const idInstallation = resInstalacao.data.installation.id

        if (this.form.arquivo_conta) {
          try {
            const billForm = new FormData()
            billForm.append('bill_pdf', this.form.arquivo_conta)
            if (this.dadosExtraidos) {
              billForm.append('extracted_json', JSON.stringify(this.dadosExtraidos))
            }
            const resBill = await api.post(
              'energy/installations/' + idInstallation + '/bill-pdf',
              billForm
            )
            if (resBill.data.status !== 'success') {
              this.$toast.warning(resBill.data.message || 'Cadastro concluído, mas houve erro ao enviar a fatura.')
            }
          } catch (e) {
            this.$toast.warning('Cadastro concluído, mas houve erro ao enviar a fatura. Reenvie pela tela da instalação.')
          }
        }

        if (this.modoInstalacaoExtra) {
          this.$router.push('/energia/clientes/' + idClient)
          this.$toast.success('Instalação cadastrada com sucesso!')
        } else {
          this.$router.push('/energia/clientes')
          this.$toast.success('Cadastro realizado com sucesso!')
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Erro ao concluir o cadastro, tente novamente em instantes.'
        this.$toast.error(msg)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div class="page-title-box d-flex align-items-center mb-3">
      <router-link :to="rotaCancelar" class="btn btn-sm btn-light mr-3">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: rgba(0, 0, 0, 1);">
          <path d="M12.707 17.293 8.414 13H18v-2H8.414l4.293-4.293-1.414-1.414L4.586 12l6.707 6.707z"></path>
        </svg>
      </router-link>
      <h3 class="mb-0">{{ tituloPagina }}</h3>
    </div>

    <div v-if="carregandoCliente" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando...</span>
      </div>
    </div>

    <template v-else>
      <div class="mb-3">
        <StepperNav
          :etapas="etapas"
          :atual="etapaAtual"
          :concluidas="etapasConcluidas"
          @ir-para="irParaEtapa"
        />
      </div>

      <div class="card">
        <div class="card-body">
          <div class="row justify-content-center">
            <div class="col-md-8">

              <!-- ===== ETAPA 1 — PDF da conta ===== -->
              <div v-show="etapaAtual === 1">
                <h5 class="mb-1">Conta de energia</h5>
                <p class="text-muted font-size-13 mb-4">
                  Envie o arquivo da fatura do seu cliente, de preferência em PDF para leitura automática.
                </p>

                <UploadZone
                  label="Arquivo da conta de energia"
                  accept=".pdf"
                  :max-mb="100"
                  :value="form.arquivo_conta_nome"
                  hint="Arraste o arquivo da fatura ou clique para selecionar"
                  @change="onContaUpload"
                  @remover="onContaRemover"
                />

                <hr class="mt-4" />
                <div class="d-flex justify-content-between">
                  <button class="btn btn-light" @click="voltar">Cancelar</button>
                  <button
                    class="btn btn-primary"
                    :disabled="!form.arquivo_conta"
                    @click="avancar"
                  >
                    Avançar →
                  </button>
                </div>
              </div>

              <!-- ===== ETAPA 2 — Extração ===== -->
              <div v-show="etapaAtual === 2">
                <h5 class="mb-1">Extração automática</h5>
                <p class="text-muted font-size-13 mb-4">
                  Lendo o PDF e identificando os dados da conta.
                </p>

                <div v-if="extraindo" class="text-center py-5">
                  <div class="spinner-border text-primary mb-3" role="status">
                    <span class="sr-only">Carregando...</span>
                  </div>
                  <p class="font-size-14 mb-1 font-weight-semibold">Analisando sua conta de energia...</p>
                  <p class="font-size-12 text-muted mb-0">Isso pode levar alguns segundos.</p>
                </div>

                <div v-else-if="erroExtracao" class="alert alert-danger">
                  <div class="d-flex align-items-center mb-2">
                    <i class="bx bx-error-circle font-size-20 mr-2"></i>
                    <strong>Erro ao processar o PDF</strong>
                  </div>
                  <p class="mb-3 font-size-13">{{ erroExtracao }}</p>
                  <button class="btn btn-sm btn-primary" @click="extrairEAvancar">
                    <i class="bx bx-refresh mr-1"></i> Tentar novamente
                  </button>
                </div>

                <div v-else-if="dadosExtraidos" class="alert alert-success">
                  <i class="bx bx-check-circle mr-1"></i>
                  Dados extraídos com sucesso! Avançando...
                </div>

                <hr class="mt-4" />
                <div class="d-flex justify-content-between">
                  <button class="btn btn-light" :disabled="extraindo" @click="voltar">← Voltar</button>
                </div>
              </div>

              <!-- ===== ETAPA 3 — Simulação ===== -->
              <div v-show="etapaAtual === 3">
                <h5 class="mb-1">Simulação de economia</h5>
                <p class="text-muted font-size-13 mb-4">
                  {{ modoManual ? 'Preencha os dados abaixo para simular a economia.' : 'Estimativa de economia com base nos dados extraídos da fatura.' }}
                </p>

                <!-- Formulário manual (arquivo não era PDF) -->
                <div v-if="modoManual" class="card border mb-4">
                  <div class="card-header py-2 font-weight-semibold font-size-13">Dados para simulação</div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="font-size-13 font-weight-semibold">Concessionária</label>
                        <select v-model="form.concessionaria_id" class="form-control">
                          <option value="">Selecione...</option>
                          <option v-for="c in concessionarias" :key="c.id" :value="c.id">{{ c.nome }}</option>
                        </select>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="font-size-13 font-weight-semibold">Tipo de instalação</label>
                        <select v-model="form.tipo_fase" class="form-control">
                          <option value="">Selecione...</option>
                          <option value="monofasico">Monofásico</option>
                          <option value="bifasico">Bifásico</option>
                          <option value="trifasico">Trifásico</option>
                        </select>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="font-size-13 font-weight-semibold">Consumo médio (kWh)</label>
                        <input v-model="form.kwh_avg" type="number" min="0" class="form-control" placeholder="Ex: 250" />
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="font-size-13 font-weight-semibold">Valor da última fatura (R$)</label>
                        <input v-model="form.valor_fatura" type="number" min="0" step="0.01" class="form-control" placeholder="Ex: 150.00" />
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="!simDadosValidos && !modoManual" class="alert alert-warning font-size-13">
                  <i class="bx bx-info-circle mr-1"></i>
                  Não foi possível calcular a simulação com os dados desta fatura.
                  Você pode avançar mesmo assim e preencher os dados manualmente.
                </div>
                <div v-if="!simDadosValidos && modoManual && extractedJsonEfetivo === null" class="alert alert-info font-size-13">
                  <i class="bx bx-info-circle mr-1"></i>
                  Preencha os campos acima para ver a simulação.
                </div>

                <template v-if="simDadosValidos">
                  <!-- Card da fatura -->
                  <div v-if="dadosFatura" class="card border mb-3">
                    <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center py-2">
                      <span class="font-weight-semibold font-size-13 text-uppercase">{{ dadosFatura.concessionaria }}</span>
                      <span v-if="dadosFatura.referencia" class="font-size-12 opacity-75">Ref. {{ dadosFatura.referencia }}</span>
                    </div>
                    <div v-if="dadosFatura.nome || dadosFatura.endereco" class="card-body py-2 border-bottom">
                      <div v-if="dadosFatura.nome" class="font-weight-semibold font-size-14">{{ dadosFatura.nome }}</div>
                      <div v-if="dadosFatura.endereco" class="text-muted font-size-13">{{ dadosFatura.endereco }}</div>
                      <div v-if="dadosFatura.cidade" class="text-muted font-size-13">
                        {{ dadosFatura.cidade }}<span v-if="dadosFatura.cep"> · CEP {{ dadosFatura.cep }}</span>
                      </div>
                    </div>
                    <div class="card-footer bg-light d-flex flex-wrap" style="gap: 24px;">
                      <div v-if="dadosFatura.totalFatura" class="d-flex flex-column">
                        <small class="text-muted text-uppercase font-size-11" style="letter-spacing:.04em">Total da fatura</small>
                        <strong class="font-size-14">R$ {{ dadosFatura.totalFatura }}</strong>
                      </div>
                      <div v-if="dadosFatura.vencimento" class="d-flex flex-column">
                        <small class="text-muted text-uppercase font-size-11" style="letter-spacing:.04em">Vencimento</small>
                        <strong class="font-size-14">{{ dadosFatura.vencimento }}</strong>
                      </div>
                      <div class="d-flex flex-column">
                        <small class="text-muted text-uppercase font-size-11" style="letter-spacing:.04em">Consumo</small>
                        <strong class="font-size-14">{{ fmtKwh(consumoUltima) }} kWh</strong>
                      </div>
                    </div>
                  </div>

                  <!-- Tabela última fatura -->
                  <h6 class="font-size-12 text-uppercase text-muted mb-2">1 — Estimativa (fatura analisada)</h6>
                  <div class="table-responsive mb-3">
                    <table class="table table-bordered table-sm font-size-13 mb-0">
                      <thead class="bg-primary text-white">
                        <tr>
                          <th></th>
                          <th
                            v-for="b in bandeiras"
                            :key="b.key"
                            class="text-center"
                            :style="bandeiraBgStyle(b.key)"
                          >{{ b.label }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="linha in linhasUltima"
                          :key="linha.key"
                          :class="{ 'table-primary font-weight-bold': linha.destaque }"
                        >
                          <td>{{ linha.label }}</td>
                          <td v-for="(v, i) in linha.valores" :key="i" class="text-center">
                            R$ {{ fmtR$(v) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Tabela média 12 meses -->
                  <template v-if="linhasMedia">
                    <h6 class="font-size-12 text-uppercase text-muted mb-2">2 — Estimativa (consumo médio 12 meses)</h6>
                    <p class="font-size-12 text-muted mb-2">
                      Consumo médio: <strong>{{ fmtKwh(consumoMedioVal) }} kWh</strong>
                    </p>
                    <div class="table-responsive mb-3">
                      <table class="table table-bordered table-sm font-size-13 mb-0">
                        <thead class="bg-primary text-white">
                          <tr>
                            <th></th>
                            <th
                              v-for="b in bandeiras"
                              :key="b.key"
                              class="text-center"
                              :style="bandeiraBgStyle(b.key)"
                            >{{ b.label }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="linha in linhasMedia"
                            :key="linha.key"
                            :class="{ 'table-primary font-weight-bold': linha.destaque }"
                          >
                            <td>{{ linha.label }}</td>
                            <td v-for="(v, i) in linha.valores" :key="i" class="text-center">
                              R$ {{ fmtR$(v) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </template>

                  <p class="text-muted font-size-11">
                    Estimativa com base na tarifa da fatura enviada e no desconto exclusivo da AXEN.
                  </p>
                </template>

                <hr />
                <div class="d-flex justify-content-between">
                  <button class="btn btn-light" @click="voltar">← Voltar</button>
                  <button class="btn btn-primary" @click="avancar">Próximo →</button>
                </div>
              </div>

              <!-- ===== ETAPA 4 — Dados pessoais ===== -->
              <div v-show="etapaAtual === 4 && !modoInstalacaoExtra">
                <h5 class="mb-1">Dados pessoais</h5>
                <p class="text-muted font-size-13 mb-4">
                  Confira e complete os dados do titular.
                </p>

                <div class="row">
                  <div class="col-12 mb-3">
                    <label class="font-size-13 font-weight-semibold d-block mb-2">Tipo de cliente <span class="text-danger">*</span></label>
                    <div class="btn-group mb-3">
                      <button type="button" class="btn" :class="form.person_type === 'pf' ? 'btn-primary' : 'btn-light'" @click="setPersonType('pf')">
                        CPF <span class="font-size-11">(Pessoa física)</span>
                      </button>
                      <button type="button" class="btn" :class="form.person_type === 'pj' ? 'btn-primary' : 'btn-light'" @click="setPersonType('pj')">
                        CNPJ <span class="font-size-11">(Pessoa jurídica)</span>
                      </button>
                    </div>
                    <div>
                      <label class="font-size-13 font-weight-semibold">{{ form.person_type === 'pj' ? 'CNPJ' : 'CPF' }} <span class="text-danger">*</span></label>
                      <input :key="form.person_type" v-model="form.person_number" v-mask="personNumberMask" type="text" class="form-control" inputmode="numeric" autocomplete="off" />
                    </div>
                  </div>

                  <div class="col-12 mb-3">
                    <label class="font-size-13 font-weight-semibold">{{ nomeRazaoLabel }} <span class="text-danger">*</span></label>
                    <input v-model="form.nome" type="text" class="form-control" autocomplete="off" />
                  </div>

                  <div class="col-md-6 mb-3">
                    <label class="font-size-13 font-weight-semibold">Telefone / WhatsApp <span class="text-danger">*</span></label>
                    <input v-model="form.telefone" v-mask="'(##) #####-####'" type="text" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="font-size-13 font-weight-semibold">E-mail <span class="text-danger">*</span></label>
                    <input v-model="form.email" type="email" class="form-control" autocomplete="off" />
                  </div>

                  <template v-if="form.person_type === 'pf'">
                    <div class="col-md-4 mb-3">
                      <label class="font-size-13 font-weight-semibold">Data de nascimento</label>
                      <input v-model="form.data_nascimento" v-mask="'##/##/####'" type="text" class="form-control" placeholder="DD/MM/AAAA" autocomplete="off" />
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="font-size-13 font-weight-semibold">Nacionalidade <span class="text-danger">*</span></label>
                      <input v-model="form.nacionalidade" type="text" class="form-control" autocomplete="off" />
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="font-size-13 font-weight-semibold">Estado civil <span class="text-danger">*</span></label>
                      <select v-model="form.estado_civil" class="form-control">
                        <option value="">Selecione...</option>
                        <option value="solteiro">Solteiro(a)</option>
                        <option value="casado">Casado(a)</option>
                        <option value="divorciado">Divorciado(a)</option>
                        <option value="viuvo">Viúvo(a)</option>
                        <option value="uniao_estavel">União estável</option>
                        <option value="separado">Separado(a)</option>
                      </select>
                    </div>
                  </template>

                  <div class="col-12 mb-3">
                    <label class="font-size-13 font-weight-semibold">Observações internas</label>
                    <textarea v-model="form.observacoes" class="form-control" rows="3" autocomplete="off"></textarea>
                  </div>
                </div>

                <hr />
                <div class="d-flex justify-content-between">
                  <button class="btn btn-light" @click="voltar">← Voltar</button>
                  <button class="btn btn-primary" @click="avancar">Próximo →</button>
                </div>
              </div>

              <!-- ===== ETAPA 5 — Documentos ===== -->
              <div v-show="etapaAtual === 5 && !modoInstalacaoExtra">
                <h5 class="mb-1">Documentos</h5>
                <p class="text-muted font-size-13 mb-4">
                  Envie o documento de identificação do titular. Aceito: imagens ou PDF. Máximo 100MB por arquivo.
                </p>

                <div class="btn-group mb-4">
                  <button type="button" class="btn" :class="form.tipo_documento === 'cnh' ? 'btn-primary' : 'btn-light'" @click="form.tipo_documento = 'cnh'">
                    <i class="bx bx-card mr-1"></i> CNH
                  </button>
                  <button type="button" class="btn" :class="form.tipo_documento === 'rg' ? 'btn-primary' : 'btn-light'" @click="form.tipo_documento = 'rg'">
                    <i class="bx bx-id-card mr-1"></i> RG
                  </button>
                </div>

                <div class="row mb-4">
                  <div class="col-md-6 mb-3">
                    <p class="font-size-13 font-weight-semibold mb-2">
                      Frente <span class="text-danger">*</span>
                    </p>
                    <UploadZone
                      :label="'Frente da ' + (form.tipo_documento === 'cnh' ? 'CNH' : 'RG')"
                      accept="image/*,.pdf"
                      :max-mb="100"
                      :value="form.arquivo_doc_frente_nome"
                      @change="file => onDocUpload('arquivo_doc_frente', file)"
                      @remover="onDocRemover('arquivo_doc_frente')"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <p class="font-size-13 font-weight-semibold mb-2">
                      Verso
                      <span v-if="form.tipo_documento === 'rg'" class="text-danger">*</span>
                      <span v-else class="text-muted font-size-11">(opcional para CNH)</span>
                    </p>
                    <UploadZone
                      :label="'Verso da ' + (form.tipo_documento === 'cnh' ? 'CNH' : 'RG')"
                      accept="image/*,.pdf"
                      :max-mb="100"
                      :value="form.arquivo_doc_verso_nome"
                      @change="file => onDocUpload('arquivo_doc_verso', file)"
                      @remover="onDocRemover('arquivo_doc_verso')"
                    />
                  </div>
                </div>

                <hr />
                <div class="d-flex justify-content-between">
                  <button class="btn btn-light" @click="voltar">← Voltar</button>
                  <button class="btn btn-primary" @click="avancar">Próximo →</button>
                </div>
              </div>

              <!-- ===== ETAPA 6 — Instalação ===== -->
              <div v-show="etapaAtual === 6">
                <h5 class="mb-1">Dados da instalação</h5>
                <p class="text-muted font-size-13 mb-4">
                  Confira e complete os dados da unidade consumidora extraídos do PDF.
                </p>

                <div v-if="modoInstalacaoExtra && clienteExistente" class="alert alert-info font-size-13 mb-4">
                  <i class="bx bx-user mr-1"></i>
                  Cliente: <strong>{{ clienteExistente.name }}</strong>
                  <span v-if="clienteExistente.person_number" class="text-muted">
                    · {{ formatCpfCnpjDisplay(clienteExistente.person_number) }}
                  </span>
                </div>

                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label>Concessionária <span class="text-danger">*</span></label>
                    <select v-model="form.concessionaria_id" class="form-control" autocomplete="off">
                      <option value="">Selecione...</option>
                      <option v-for="c in concessionarias" :key="c.id" :value="c.id">{{ c.nome }}</option>
                    </select>
                    <small v-if="!form.concessionaria_id && concessionariaNaoIdentificada" class="d-block mt-1 text-warning font-size-12">
                      <i class="bx bx-info-circle"></i>
                      Não identificamos automaticamente. Fatura emitida por: <strong>{{ concessionariaNaoIdentificada }}</strong>
                    </small>
                  </div>
                  <div class="col-md-3 mb-3">
                    <label>Nº de instalação <span class="text-danger">*</span></label>
                    <input v-model="form.numero_instalacao" v-mask="'#.###.###.###-##'" type="text" class="form-control" inputmode="numeric" placeholder="3.716.000.011-17" autocomplete="off" />
                  </div>
                  <div class="col-md-3 mb-3">
                    <label>Tipo de instalação</label>
                    <select v-model="form.tipo_fase" class="form-control">
                      <option value="">Selecione...</option>
                      <option value="monofasico">Monofásico</option>
                      <option value="bifasico">Bifásico</option>
                      <option value="trifasico">Trifásico</option>
                    </select>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label>Mês de referência</label>
                    <input v-model="form.mes_referencia" type="month" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-4 mb-3">
                    <label>Valor da fatura (R$)</label>
                    <input v-model="form.valor_fatura" type="number" step="0.01" min="0" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-4 mb-3">
                    <label>Média kWh <span class="text-danger">*</span></label>
                    <input v-model="form.kwh_avg" type="number" step="0.01" min="0" class="form-control" placeholder="0" autocomplete="off" />
                  </div>
                </div>

                <h6 class="mt-2 mb-3">Endereço de instalação</h6>
                <div class="row">
                  <div class="col-md-3 mb-3">
                    <label>CEP <span class="text-danger">*</span></label>
                    <input v-model="form.cep" v-mask="'#####-###'" type="text" class="form-control" @blur="buscarCep(form.cep)" autocomplete="off" />
                  </div>
                  <div class="col-md-9 mb-3">
                    <label>Logradouro <span class="text-danger">*</span></label>
                    <input v-model="form.logradouro" type="text" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-3 mb-3">
                    <label>Número <span class="text-danger">*</span></label>
                    <input :value="form.numero" :disabled="form.numero === 'S/N'" type="text" class="form-control" inputmode="numeric" autocomplete="off" @input="filtrarNumero" />
                    <div class="custom-control custom-checkbox mt-1">
                      <input id="sem-numero" type="checkbox" class="custom-control-input" :checked="form.numero === 'S/N'" @change="toggleSemNumero($event.target.checked)" />
                      <label class="custom-control-label" for="sem-numero">Sem número</label>
                    </div>
                  </div>
                  <div class="col-md-9 mb-3">
                    <label>Complemento</label>
                    <input v-model="form.complemento" type="text" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-4 mb-3">
                    <label>Bairro</label>
                    <input v-model="form.bairro" type="text" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label>Cidade <span class="text-danger">*</span></label>
                    <input v-model="form.cidade" type="text" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-2 mb-3">
                    <label>UF <span class="text-danger">*</span></label>
                    <input v-model="form.estado" type="text" class="form-control" maxlength="2" autocomplete="off" />
                  </div>
                  <div class="col-12 mb-3">
                    <label>Observações</label>
                    <textarea v-model="form.obs_instalacao" class="form-control" rows="2" autocomplete="off"></textarea>
                  </div>
                </div>

                <h6 class="mt-2 mb-3">Acesso ao portal da concessionária</h6>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label>Usuário do portal</label>
                    <input v-model="form.portal_usuario" type="text" class="form-control" autocomplete="off" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label>Senha do portal</label>
                    <input v-model="form.portal_senha" type="password" class="form-control" autocomplete="new-password" />
                  </div>
                </div>

                <hr />
                <div class="d-flex justify-content-between">
                  <button class="btn btn-light" @click="voltar">← Voltar</button>
                  <button class="btn btn-primary" @click="avancar">Próximo →</button>
                </div>
              </div>

              <!-- ===== ETAPA 7 — Revisão ===== -->
              <div v-show="etapaAtual === 7">
                <h5 class="mb-1">Revisão</h5>
                <p class="text-muted font-size-13 mb-4">Confirme os dados antes de concluir o cadastro.</p>

                <h6 class="text-uppercase font-size-11 text-muted mb-2">Cliente</h6>
                <div class="row mb-4">
                  <div class="col-md-6 mb-2">
                    <small class="text-muted d-block">Nome / Razão social</small>
                    <span class="font-weight-semibold">{{ form.nome || '—' }}</span>
                  </div>
                  <div class="col-md-6 mb-2">
                    <small class="text-muted d-block">CPF / CNPJ</small>
                    <span>{{ form.person_number ? formatCpfCnpjDisplay(form.person_number) : '—' }}</span>
                  </div>
                  <template v-if="!modoInstalacaoExtra">
                    <div class="col-md-6 mb-2">
                      <small class="text-muted d-block">E-mail</small>
                      <span>{{ form.email || '—' }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                      <small class="text-muted d-block">Telefone</small>
                      <span>{{ form.telefone || '—' }}</span>
                    </div>
                    <template v-if="form.person_type === 'pf'">
                      <div v-if="form.data_nascimento" class="col-md-4 mb-2">
                        <small class="text-muted d-block">Data de nascimento</small>
                        <span>{{ form.data_nascimento }}</span>
                      </div>
                      <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Nacionalidade</small>
                        <span>{{ form.nacionalidade || '—' }}</span>
                      </div>
                      <div class="col-md-4 mb-2">
                        <small class="text-muted d-block">Estado civil</small>
                        <span>{{ form.estado_civil || '—' }}</span>
                      </div>
                    </template>
                    <div class="col-12 mb-2">
                      <small class="text-muted d-block">Documento enviado</small>
                      <span class="text-uppercase font-size-12">{{ form.tipo_documento }}</span>
                      <span v-if="form.arquivo_doc_frente_nome" class="ml-2">· {{ form.arquivo_doc_frente_nome }}</span>
                      <span v-if="form.arquivo_doc_verso_nome" class="ml-2">· {{ form.arquivo_doc_verso_nome }}</span>
                    </div>
                  </template>
                </div>

                <h6 class="text-uppercase font-size-11 text-muted mb-2">Instalação</h6>
                <div class="row mb-4">
                  <div class="col-md-4 mb-2">
                    <small class="text-muted d-block">Concessionária</small>
                    <span class="badge badge-soft-secondary font-size-13">{{ nomeConcessionaria || '—' }}</span>
                  </div>
                  <div class="col-md-4 mb-2">
                    <small class="text-muted d-block">Nº Instalação</small>
                    <span>{{ form.numero_instalacao || '—' }}</span>
                  </div>
                  <div class="col-md-4 mb-2">
                    <small class="text-muted d-block">Média kWh</small>
                    <span>{{ form.kwh_avg || '0' }}</span>
                  </div>
                  <div v-if="form.mes_referencia" class="col-md-4 mb-2">
                    <small class="text-muted d-block">Mês de referência</small>
                    <span>{{ form.mes_referencia }}</span>
                  </div>
                  <div v-if="form.valor_fatura" class="col-md-4 mb-2">
                    <small class="text-muted d-block">Valor da fatura</small>
                    <span>R$ {{ Number(form.valor_fatura).toFixed(2).replace('.', ',') }}</span>
                  </div>
                  <div v-if="form.tipo_fase" class="col-md-4 mb-2">
                    <small class="text-muted d-block">Tipo de instalação</small>
                    <span>{{ { monofasico: 'Monofásico', bifasico: 'Bifásico', trifasico: 'Trifásico' }[form.tipo_fase] || form.tipo_fase }}</span>
                  </div>
                  <div v-if="simulacao" class="col-md-4 mb-2">
                    <small class="text-muted d-block">Simulação</small>
                    <span class="badge badge-soft-success font-size-12">Calculada</span>
                  </div>
                  <div v-if="form.logradouro" class="col-12 mb-2">
                    <small class="text-muted d-block">Endereço</small>
                    <span>
                      {{ form.logradouro }}, {{ form.numero }}
                      <template v-if="form.complemento"> — {{ form.complemento }}</template>
                      <template v-if="form.bairro">, {{ form.bairro }}</template>
                      — {{ form.cidade }}/{{ form.estado }} — {{ form.cep }}
                    </span>
                  </div>
                  <div v-if="form.arquivo_conta_nome" class="col-12 mb-2">
                    <small class="text-muted d-block">PDF da fatura</small>
                    <span>{{ form.arquivo_conta_nome }}</span>
                  </div>
                  <div class="col-md-6 mb-2">
                    <small class="text-muted d-block">Usuário do portal</small>
                    <span>{{ form.portal_usuario || '—' }}</span>
                  </div>
                  <div class="col-md-6 mb-2">
                    <small class="text-muted d-block">Senha do portal</small>
                    <span>{{ form.portal_senha ? '••••••••' : '—' }}</span>
                  </div>
                </div>

                <hr />
                <div class="d-flex justify-content-between align-items-center">
                  <button class="btn btn-light" @click="voltar">← Voltar</button>
                  <button class="btn btn-primary" :disabled="saving" @click="avancar">
                    Salvar e concluir
                    <span v-if="saving" class="spinner-border spinner-border-sm ml-1"></span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </template>
  </Layout>
</template>
